/**
 * This module extends the Leaflet library in order to display a TomTom map. For full reference of the Leaflet map API
 * check <a href="http://leafletjs.com/reference.html" target="_blank">here</a>.
 *
 * @example
 *var map = L.map("map", {key: "<Your API key>"});
 *
 * @module Map
 * @class L
 */
/**
 * Initializes the map. - .map()
 *
 * @method map
 * @param {HTMLElement|String} id The div element or its id in which the map will be initialized.
 * @param {Object} [options] All the options that leaflet allows, as described here:
 *     http://leafletjs.com/reference.html#map-options Additional options, which are TomTom specific are described
 *     below.
 * @param {String} [options.key] This is an API key that can be obtained form http://developer.tomtom.com When the key
 *     is passed on the map's initialization, it is set globally for all the services. It can be overwritten with the
 *     tomtom.key method globally or per service  with tomtom.&lt;service_name&gt;.key functions. It is also possible
 *     to pass a specific key while working with instances of the services.
 * @param {String} [options.style] The base map style to be used. Should be set to one of the following values:
 *     &lt;'main', 'night'&gt;. By default, the _main_ style is used.
 * @param {String} [options.layer] The base map's layer to be used. Should be set to one of the following values:
 *     &lt;'basic', 'hybrid', 'labels'&gt;. By default, the _basic_ layer is used.
 * @param {Boolean|Object} [options.traffic] Allows to add L.TomTomTrafficLayer and L.TomTomTrafficIncidentsLayer to
 *     the map. A value of this parameter can be set to an options object as described in the traffic layer
 *     documentation. If traffic layers are added with this option, a mechanism for periodic refresh of the traffic
 *     data is provided (using a proper traffic model id handling).
 * @param {Boolean} [options.traffic.noRefresh] Disables periodic updates of traffic layers.
 * @param {Boolean|Object} [options.trafficFlow] Allows to add L.TomTomTrafficFlowLayer to the map. A value of this
 *     parameter can be set to the options object as described in the traffic flow layer documentation.
 * @param {Boolean} [options.noLabelsOnTop=false] If a traffic or traffic flow layer is overlayed on the map, then by
 *     default, the labels layer is added on top of these layers. It prevents labels from being covered by the traffic
 *     tubes or traffic flow. With this option, it is possible to disable this behaviour.
 * @param {String|Array} [options.source=raster] Sets initial source and determines which tiles types will be displayed
 *     (raster, vector). If an array is given first item is used as a default source and next items are initialized
 *     but not displayed. If MapboxGL JS dependency is not met vector layer will not initialize.
 * @param {String} [options.glyphsUrl] Sets base URL to glyphs. Glyphs are fonts in PBF required to display labels on
 *     vector maps. Url must include {fontstack} and {range} tokens. SDK distributable contains glyphs required for maps
 *     styles included in SDK.
 *```javascript
 *"glyphsUrl": "http://yourdomain/glyphs/{fontstack}/{range}.pbf"
 *```
 * @param {String} [options.spriteUrl] Sets base URL to sprite image and metadata. Extension .png, .json and scale
 *     factor will be appended at the end of URL automatically. Spite URL is required to display for example road
 *     shields on vector maps.
 *```javascript
 *"spriteUrl": "http://yourdomain/sprite/sprite"
 *```
 * @param {Boolean} [options.zoomControl=true] Show a zoom control at the top left corner of the map.
 * @param {Boolean} [options.panControl=false] Enable the pan controls at the top left corner of the map.
 * @param {Boolean} [options.zoomsliderControl=false] Shows a vertical slider to control the zoom level.
 * It is recommended to disable the default zoom control option (zoomControl=false) when you use this feature.
 * @return {map} Returns the Leaflet map object.
 * @example
 * ```html
 * <style type="text/css">
 *   html, body {
 *      width: 100%;
 *      height: 100%;
 *      margin: 0;
 *      padding: 0;
 *  }
 *
 *  #map {
 *      width: 100%;
 *      height: 100%;
 *  }
 * </style>
 *
 * <div id="map"></div>
 *
 * <script>
 *   var map = L.map("map", {key: "<Your API key>"});
 * </script>
 * ```
 */
define('display/map',[
    'leaflet',
    './svgIcon',
    './imagePath',
    'mapboxProvider',
    'security',
    'services/copyrightCaption',
    './copyright',
    './layers/trafficLayer',
    './layers/trafficFlowLayer',
    './layers/labelsLayer',
    './layers/vector/vectorLabelsLayer',
    './layers/tileLayer',
    './layers/vector/vectorLayer'
],
function(
    L,
    svgIcon,
    imagePath,
    mapboxProvider,
    security,
    copyrightCaption,
    copyright,
    traffic,
    trafficFlow,
    labelsLayer,
    VectorLabelsLayer,
    tileLayer,
    VectorLayer
) {
    'use strict';
    L.Map.mergeOptions({
        worldCopyJump: true
    });
    delete L.Map.prototype.options.minZoom;
    delete L.Map.prototype.options.maxZoom;

    /**
     * Overrides default Leaflet touch device recognition
     */
    L.Browser.touch = Boolean('ontouchstart' in window ||
		window.DocumentTouch && document instanceof window.DocumentTouch);

    /**
     * Method allows to change a source data for currently shown map. It should be one of sources provided
     * at creation as a property 'options.source'
     *
     * @method setMapSource
     * @param {String} source a source of data for a mini map
     */
    L.Map.prototype.setMapSource = function(source) {
        var baseLayers, map, requestedLayer, baseLayer;
        map = this;
        baseLayers = map._ttBaseLayers;
        requestedLayer = baseLayers[source];
        if (!requestedLayer) {
            return;
        }

        for (baseLayer in baseLayers) {
            if (baseLayers.hasOwnProperty(baseLayer)) {
                this.removeLayer(baseLayers[baseLayer]);
            }
        }
        map.addLayer(requestedLayer);
    };

    /**
     * This method tries to mitigate a bug causing background bleeding near tiles edges.
     * More info can be found here: LNS-9557
     * When this issue is fixed in Leaflet itself this method may be removed.
     */
    function replaceOriginalSetTransform() {
        var oldSetTransform = L.DomUtil.setTransform;
        L.DomUtil.setTransform = function(el, offset, scale) {
            if (el.tagName === 'IMG') {
                scale = 1.002;
            }
            oldSetTransform(el, offset, scale);
        };
    }

    replaceOriginalSetTransform();

    /**
     * @method Map.getBaseLayers
     * @return {Object} Returns base layers initialized on map
     */
    L.Map.prototype.getBaseLayers = function() {
        return this._ttBaseLayers;
    };

    /**
     * Updates options added currently to map (including inactive base layer). Only 'style' and 'layer' options
     * update supported.
     *
     * @method Map.updateLayersOptions
     * @param {Object} options List of options to be updated. Only 'style' and 'layer' update supported
     * @param {String} [options.style] The base map style to be used. Should be set to one of the following values:
     *     &lt;'main', 'night'&gt;. By default, the current style is used (i.e. no update of this property).
     * @param {String} [options.layer] The base map layer to be used. Should be set to one of the following values:
     *     &lt;'basic', 'hybrid', 'labels'&gt;. By default, the current layer is used (i.e. no update of this property).
     */
    L.Map.prototype.updateLayersOptions = function(options) {
        var alreadyUpdatedLayers = [this._ttBaseLayers.raster];
        this._ttBaseLayers.raster.updateOptions(options);

        pushToUpdatedLayers(alreadyUpdatedLayers, this._ttBaseLayers.vector, options);
        pushToUpdatedLayers(alreadyUpdatedLayers, this._ttLabelsLayers.vector, options);
        pushToUpdatedLayers(alreadyUpdatedLayers, this._ttLabelsLayers.raster, options);

        this.eachLayer(function(layer) {
            if (layer.updateOptions && alreadyUpdatedLayers.indexOf(layer) === -1) {
                layer.updateOptions(options);
            }
        });
    };

    function pushToUpdatedLayers(alreadyUpdatedLayers, layer, options) {
        if (layer) {
            layer.updateOptions(options);
            alreadyUpdatedLayers.push(layer);
        }
    }

    L.Control.Attribution.prototype.options = {
        position: 'bottomleft',
        prefix: ''
    };

    L.Icon.Default = svgIcon;
    L.Icon.Default.imagePath = imagePath.get();
    L.Marker.mergeOptions({ icon: new L.Icon.Default() });

    function removeInvalidSources(source) {
        var allowedSources = /^(raster|vector)$/;
        var sourceLenght = source.length;
        var validSource = [];
        var currentSource, i;

        for (i = 0; i < sourceLenght; i++) {
            currentSource = String(source[i]).toLowerCase();
            if (allowedSources.test(source[i])) {
                validSource.push(currentSource);
            }
        }

        return validSource;
    }

    function getSource(map) {
        var source = map.options.source || ['raster'];
        var defaultSource = ['raster'];

        if (typeof source === 'string') {
            source = [source];
        }

        source = removeInvalidSources(source);

        return source.length > 0 ? source : defaultSource;
    }

    function isOlderThanIE11() {
        return window.navigator.userAgent.indexOf('MSIE ') > -1;
    }

    function initTrafficLayer(map) {
        if (map.options.traffic) {
            map.options.layers.push(traffic(map.options.traffic));
        }
    }

    function initTrafficFlowLayer(map) {
        if (map.options.trafficFlow) {
            map.options.layers.push(trafficFlow(map.options.trafficFlow));
        }
    }

    function initLabelsLayers(map) {
        var source = getSource(map);
        var layerInstance;
        var vectorLabelsInstance;
        map._ttLabelsLayers = {};

        if (source.indexOf('raster') !== -1 && !map.options.noLabelsOnTop && !isOlderThanIE11()) {
            //This will not work on IE < 11 due to not supported CSS: pointer-events: none;
            layerInstance = labelsLayer(map.options);
            layerInstance.registerEventHandlers(map);
            L.stamp(layerInstance);
            map._ttLabelsLayers.raster = layerInstance;
        }

        if (source.indexOf('vector') !== -1 && !map.options.noLabelsOnTop && mapboxProvider.isMapboxSet()) {
            vectorLabelsInstance = new VectorLabelsLayer(map.options);
            vectorLabelsInstance.registerEventHandlers(map);
            L.stamp(vectorLabelsInstance);
            map._ttLabelsLayers.vector = vectorLabelsInstance;
        }
    }

    function resolveMapboxSupport(map) {
        var defaultFallback = 'error';
        var allowedFallbacks = /^(error|raster)$/;
        var source = getSource(map);
        var fallback = map.options.vector ? String(map.options.vector.fallback).toLowerCase() : '';
        fallback = allowedFallbacks.test(fallback) ? fallback : defaultFallback;

        if (source.indexOf('vector') === -1 || mapboxProvider.isMapboxSupported()) {
            return;
        }

        switch (fallback) {
        case 'raster':
            map.options.source = ['raster'];
            break;
        case 'error':
            throw new Error('Unable to initialize vector layer - browser doesn\'t support WebGL!');
        default:
            throw new Error('Something went wrong with vectorFallback!');
        }
    }

    function initBaseLayer(map) {
        var source = getSource(map);
        var baseLayer;
        map._ttBaseLayers = {};

        if (source.indexOf('raster') !== -1) {
            map._ttBaseLayers.raster = tileLayer(map.options);
            L.stamp(map._ttBaseLayers.raster);
        }

        if (mapboxProvider.tryToSetMapbox() && source.indexOf('vector') !== -1) {
            map._ttBaseLayers.vector = new VectorLayer(map.options);
            L.stamp(map._ttBaseLayers.vector);
        }

        switch (source[0]) {
        case 'vector':
            if (map._ttBaseLayers.vector === undefined) {
                throw 'Can not add vector layer. Mapbox-gl-js dependency not met.'
            }
            baseLayer = map._ttBaseLayers.vector;
            break;
        case 'raster':
            baseLayer = map._ttBaseLayers.raster;
            break;
        default:
            throw 'Something went wrong with sources!';
        }

        map.options.layers = [baseLayer];
    }

    function initHook() {
        var map = this;

        resolveMapboxSupport(map);

        security.setKey(map.options.key);
        initBaseLayer(map);
        initTrafficLayer(map);
        initTrafficFlowLayer(map);
        initLabelsLayers(map);

        if (map.getZoom() === undefined) {
            map.setView([0, 0], 2);
        }
        if (!map.options.maxBounds) {
            map.setMaxBounds(L.latLngBounds([[-90, -18000], [90, 18000]]));
        }
        copyrightCaption().go(function(attribution) {
            if (map.attributionControl) {
                map.attributionControl.addAttribution('<a href=\'#\' class=\'tomtomAttribution\'>' + attribution + '</a>');
                copyright.addTo(map);
            }
        });
    }

    L.Map.addInitHook(initHook);

    return L;
});

